import React from 'react';
import { eur } from '../format.js';
import Info from './Info.jsx';

/**
 * Carte marge - ce que garde la plateforme.
 *
 * Séparée du résultat à dessein : le client voit un prix, l'utilisateur voit sa
 * marge. Les mélanger dans une seule carte rendrait la copie de la réponse
 * risquée.
 *
 * Les lignes se somment exactement en marge nette, ce qui rend le total
 * vérifiable de tête. `officialRate` sert à ramener en euros ce que le moteur
 * exprime en CFA - à la parité, jamais au taux appliqué, sans quoi le spread
 * serait compté deux fois.
 */
export default function CarteMarge({ r, officialRate }) {
  const sortant = r.direction === 'out';

  const fraisService = sortant ? r.fraisEur : r.fraisCfa / officialRate;
  const operateurAbsorbe =
    r.feeMode === 'acharge' && r.opTotal > 0 ? r.opTotal / officialRate : 0;

  const lignes = [
    ['Frais de service', eur(fraisService)],
    ['Marge de change', eur(r.margeChange)],
    operateurAbsorbe > 0 && ['Frais opérateur absorbés', `− ${eur(operateurAbsorbe)}`],
  ].filter(Boolean);

  const negative = r.margeNette < 0;

  // Carte sombre : le design system la distingue ainsi du résultat client. Ce
  // que garde la plateforme n'est pas ce que voit le client, et la séparation
  // visuelle évite de copier l'un pour l'autre.
  return (
    <section
      className="carte"
      aria-label="Marge de la plateforme"
      style={{ background: 'var(--couleur-encre)', borderColor: 'var(--couleur-encre)', color: 'var(--couleur-sur-encre)' }}
    >
      <div className="etiquette" style={{ color: 'var(--couleur-sur-encre-douce)' }}>
        Marge indicative
        <Info titre="Marge indicative">
          Vos frais de service, plus la marge de change, moins les frais
          opérateur que vous absorbez. Elle peut être négative, et l’application
          l’affiche telle quelle.
          <strong style={{ display: 'block', marginTop: 'var(--espace-2)' }}>
            Elle ne correspondra pas à ce qu’inscrira le livre.
          </strong>
          Ici, la marge de change se mesure contre la <strong>parité officielle</strong>.
          Dans le livre, elle se mesure contre votre <strong>coût de revient réel</strong>,
          et sur un achat d’euros elle n’est pas encore acquise : elle ne le
          deviendra qu’à la revente. Les deux chiffres sont justes, ils ne
          répondent pas à la même question.
        </Info>
      </div>
      <div
        className="chiffre"
        style={{
          fontSize: '1.5rem', fontWeight: 700, margin: 'var(--espace-1) 0 var(--espace-3)',
          // L'ocre est l'emphase du produit, et 1,5 rem en gras le place dans
          // le grand texte, où son 4,47:1 sur l'encre est conforme. Le rouge
          // passe par sa variante claire : le rouge plein ne tient que 2,37:1
          // sur ce fond.
          color: negative ? 'var(--couleur-alerte-claire)' : 'var(--couleur-emphase)',
        }}
      >
        {eur(r.margeNette)}
      </div>

      <dl style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--espace-1) var(--espace-3)',
        margin: 0, fontSize: '0.875rem',
      }}>
        {lignes.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ color: 'var(--couleur-sur-encre-douce)' }}>{k}</dt>
            <dd className="chiffre" style={{ margin: 0, textAlign: 'right' }}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>

      {negative && (
        <p style={{ marginTop: 'var(--espace-3)', marginBottom: 0, fontSize: '0.8rem', color: 'var(--couleur-alerte-claire)' }}>
          Cette cotation vous coûte de l’argent.
        </p>
      )}

      {/* Dit à l'écran ce que le wiki disait déjà. Sans cela, l'écart entre
          cette carte et l'écran Position se lit comme une erreur de calcul. */}
      <p style={{ marginTop: 'var(--espace-3)', marginBottom: 0, fontSize: '0.75rem', color: 'var(--couleur-sur-encre-douce)' }}>
        Estimation de cette cotation, mesurée contre la parité. Le livre
        comptabilise contre votre coût de revient réel.
      </p>
    </section>
  );
}
